import React from "react";
import { Sparkles, Repeat, Check, Pause, X } from "lucide-react";

export default function WeeklyPlans({ onStart }) {
    return (
        <section
            id="weekly-plans"
            data-testid="weekly-plans-section"
            className="bg-cream pb-20 md:pb-28 px-6 sm:px-8"
        >
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                {/* Image side */}
                <div
                    data-testid="weekly-plans-image"
                    className="relative reveal"
                >
                    <div className="absolute -inset-3 bg-gold/20 rounded-[28px] blur-2xl" />
                    <img
                        src="https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/aiukmn0c_file_00000000017071f5947ca391ac30a810.png"
                        alt="Caribbean Meal Prep — ready to deliver every week"
                        className="relative rounded-[24px] shadow-[0_18px_50px_rgba(13,40,24,0.25)] w-full h-auto"
                        draggable={false}
                    />
                </div>

                {/* Subscribe side */}
                <div className="reveal" style={{ transitionDelay: "120ms" }}>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-goldDark text-[11px] font-bold tracking-widest uppercase">
                        <Repeat size={12} /> Subscribe &amp; Save
                    </span>
                    <h2 className="mt-4 font-serif font-black text-3xl sm:text-4xl md:text-5xl leading-tight text-navy">
                        Save an extra{" "}
                        <span className="text-forest italic">10% every week.</span>
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-muted2 leading-relaxed max-w-lg">
                        Subscribe to your weekly Caribbean meal-prep box and get{" "}
                        <span className="font-bold text-forest">
                            10% off on top
                        </span>{" "}
                        of any volume discount you've already unlocked
                        (3 / 6 / 9 / 12 plates).
                    </p>

                    {/* Stacking example */}
                    <div
                        data-testid="subscribe-example"
                        className="mt-6 rounded-2xl bg-white border border-mint p-5 sm:p-6"
                    >
                        <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                            Example · 6-plate box
                        </p>
                        <ul className="mt-3 space-y-1.5 text-sm text-navy/90">
                            <li className="flex justify-between">
                                <span>Subtotal</span>
                                <span className="tabular-nums">$77.94</span>
                            </li>
                            <li className="flex justify-between text-forest">
                                <span>Volume discount · 10%</span>
                                <span className="tabular-nums">−$7.79</span>
                            </li>
                            <li className="flex justify-between text-forest font-bold">
                                <span>Subscriber bonus · 10%</span>
                                <span className="tabular-nums">−$7.79</span>
                            </li>
                            <li className="flex justify-between border-t border-mint pt-2 mt-2 font-bold text-navy">
                                <span>You pay</span>
                                <span className="font-display text-2xl text-forest tabular-nums">
                                    $62.36
                                </span>
                            </li>
                        </ul>
                        <p className="mt-3 text-xs text-muted2">
                            That's <span className="font-bold text-burnt">$15.58</span>{" "}
                            saved every week — about{" "}
                            <span className="font-bold text-burnt">$810/year</span>{" "}
                            in your pocket.
                        </p>
                    </div>

                    {/* How it works bullets */}
                    <ul className="mt-6 space-y-2.5 text-sm text-navy/90">
                        <Bullet text="No sign-up fee. Free to subscribe." />
                        <Bullet text="Auto-billed every Friday at 10 PM (same as the order cutoff)." />
                        <Bullet text="Mix &amp; match meals every week — your weekly menu, your way." />
                        <Bullet
                            icon={Pause}
                            text="Skip a week anytime from your account before Friday 10 PM."
                        />
                        <Bullet
                            icon={X}
                            text="No commitment — cancel any time, no questions asked."
                        />
                    </ul>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <button
                            data-testid="weekly-plans-start"
                            onClick={onStart}
                            className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-7 py-3.5 transition-all active:scale-95 shadow-[0_10px_28px_rgba(244,164,53,0.35)]"
                        >
                            <Sparkles
                                size={16}
                                className="inline -mt-0.5 mr-1.5"
                            />
                            Start subscribing
                        </button>
                        <span className="text-xs text-muted2 self-center">
                            3-plate minimum applies · Greater Boston only
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Bullet({ text, icon: Icon = Check }) {
    return (
        <li className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-mint">
                <Icon size={12} className="text-forest" strokeWidth={3} />
            </span>
            <span dangerouslySetInnerHTML={{ __html: text }} />
        </li>
    );
}
